import { useState } from "react";
import { motion } from "framer-motion";
import { Seo } from "@/seo/Seo";
import { PageHero } from "@/layouts/PageHero";
import { Section } from "@/components/common/Section";
import { Container } from "@/components/common/Container";
import { CtaButton } from "@/components/common/CtaButton";
import { ProjectCard } from "@/components/portfolio/ProjectCard";
import { DetailCta } from "@/components/detail/DetailCta";
import { caseStudies } from "@/data/caseStudies.data";
import { fadeUp, staggerContainer, viewportOnce } from "@/animations/motion";
import { cn } from "@/lib/utils";
import { siteConfig } from "@/config/site.config";

const categories = ["All", ...Array.from(new Set(caseStudies.map((c) => c.category)))];

const businessOutcomes = [
  "Faster business processes",
  "Better operational visibility",
  "AI-powered automation",
  "Scalable cloud architecture",
  "Improved customer experience",
  "Enterprise-grade security",
];

export default function Portfolio() {
  const [filter, setFilter] = useState("All");
  const filtered =
    filter === "All"
      ? caseStudies
      : caseStudies.filter((c) => c.category === filter);

  return (
    <>
      <Seo
        title="Portfolio"
        description="Explore representative software solutions demonstrating our expertise in enterprise applications, AI integration, workflow automation, mobile applications and digital transformation across multiple industries."
        path="/portfolio"
      />
      <PageHero
        eyebrow="Featured Projects"
        titleLines={["Software Solutions", "Built for Real Business Challenges."]}
        description="Explore representative software solutions that showcase our approach to solving real business challenges. These demonstration projects highlight our design, engineering and problem-solving capabilities while our client portfolio continues to grow."
      >
        <div className="flex flex-col gap-3 sm:flex-row">
          <CtaButton to={siteConfig.primaryCta.path} withArrow magnetic>
            Book a Free Consultation
          </CtaButton>
          <CtaButton to="/case-studies" variant="outline">
            Read Case Studies
          </CtaButton>
        </div>
      </PageHero>

      <Section spacing="compact" className="pt-0">
        <Container>
          {/* Category filter */}
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter projects">
            {categories.map((cat) => (
              <button
                key={cat}
                role="tab"
                aria-selected={filter === cat}
                data-testid={`portfolio-filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setFilter(cat)}
                className={cn(
                  "rounded-full border px-4 py-2 text-sm font-medium transition-all duration-300",
                  filter === cat
                    ? "border-transparent bg-primary text-primary-foreground"
                    : "border-border bg-card text-foreground hover:border-primary/30",
                )}
              >
                {cat}
              </button>
            ))}
          </div>

          <motion.div
            key={filter}
            variants={staggerContainer(0.08, 0.05)}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            className="mt-10 grid gap-6 md:grid-cols-2"
          >
            {filtered.length > 0 ? (

              filtered.map((project) => (

                <motion.div key={project.slug} variants={fadeUp}>
                  <ProjectCard project={project} />
                </motion.div>

              ))

            ) : (

              <motion.div variants={fadeUp} className="col-span-full">

                <div className="rounded-3xl border border-dashed bg-card p-12 text-center">

                  <h3 className="text-xl font-semibold">
                    No projects available
                  </h3>

                  <p className="mt-3 text-muted-foreground">
                    We don't have any showcase projects in this category yet.
                    Please check back soon or explore another category.
                  </p>

                  <div className="mt-6">

                    <CtaButton
                      to="/contact"
                      variant="outline"
                    >
                      Discuss Your Project
                    </CtaButton>

                  </div>

                </div>

              </motion.div>

            )}
            {/* {
              filtered.map((project) => (
                <motion.div key={project.slug} variants={fadeUp}>
                  <ProjectCard project={project} />
                </motion.div>
              ))
            } */}
          </motion.div>
          {/** added by sudip */}
          {/* Business Outcomes */}
          <div className="mt-16 rounded-3xl border bg-card p-8">
            <div className="text-center">

              <h2 className="text-2xl font-semibold">
                Business Outcomes Delivered
              </h2>

              <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
                Every solution we build is designed to improve operational
                efficiency, enhance decision-making and support long-term
                business growth.
              </p>
            </div>

            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">

              {businessOutcomes.map((item) => (

                <div
                  key={item}
                  className="flex items-center gap-3 rounded-xl border bg-background px-4 py-3"
                >
                  <span className="text-primary text-lg">
                    ✓
                  </span>

                  <span className="font-medium">
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>

        </Container>
      </Section>

      <DetailCta note="Ready to turn your business idea into a scalable software solution?" />
    </>
  );
}
